// =============================================================================
// client/src/components/flux/CampaignReport.jsx | S103 — the Report tab, shared
// =============================================================================
// Extracted VERBATIM from CampaignDetail.jsx's `tab === 'report'` branch when
// the kid got his own copy of the report (CCF_PPP_Updates WO2, Jake 2026-09-06:
// "It should look just like the report page we see on the back end, except it
// should not show cold, unopened, or bounced ... read only only").
//
// PRESENTATIONAL. No fetch, and no data state of its own (each school row
// holds only whether it is open). CampaignDetail passes the
// state it already owns; MyOutreach passes the player rollup. Every number
// still comes from the server (invariant 7 — the browser computes nothing).
//
// `audience`:
//   'staff'  — today's markup, byte for byte: the bounced strip cell, the Cold
//              tier, the unmatched-engagement note, "not opened", the bounce
//              markers, "sent, none engaged" in the legend.
//   'player' — those branches are ABSENT from the markup, not hidden. The
//              player rollup never carries the data anyway (the builder drops
//              it before aggregation); this just means there is nothing here
//              that could render it if it did.
//
// stateFill() moved here with the JSX and is UNCHANGED — the hex ramp is the
// data legend, not chrome.
import { memo, useState } from 'react';
import stateData from '../../lib/stateData.json';
import { PANEL, DASH, pill, H2, LABEL, BODY_MUTED } from '../../lib/flux/board.js';
import SnapshotNote, { PendingPanel } from './SnapshotNote.jsx';

// One school in the tree. Its coach rows mount only while it is open: a big
// campaign has thousands of coaches, and mounting them all inside closed
// <details> made every expand reflow the whole page. memo keeps one school's
// toggle from re-rendering the others.
const SchoolRow = memo(function SchoolRow({ school: s, player }) {
  const [open, setOpen] = useState(false);
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer text-sm text-[#0E2A20]">
        {s.school || '(unknown)'} — {s.sent}/{s.total} coaches · {s.opened} opens · {s.clicked} clicked
        {!player && s.total > s.sent ? <span className="text-[#5A5A5A]"> · {s.total - s.sent} not yet sent</span> : null}
      </summary>
      {open ? (
        <ul className="pl-4 mt-1 space-y-0.5">
          {s.coaches.map((c, index) => (
            <li key={`${c.name}-${c.title || ''}-${index}`} className={`${BODY_MUTED}`}>
              <span>{c.tier === 'hot' ? '🔥' : c.tier === 'interested' ? '◆' : c.tier === 'warm' ? '⚡' : '•'} </span>
              {c.name}{c.title ? ` — ${c.title}` : ''}
              {c.score != null ? ` · score ${c.score}` : ''}
              {' · '}
              {player ? (
                <>
                  viewed
                  {c.clicked ? ' · clicked' : ''}
                </>
              ) : (
                <>
                  {c.opened ? 'viewed' : 'not opened'}
                  {c.clicked ? ' · clicked' : ''}
                  {/* 'active' is every normal coach; show only the ones that exclude. */}
                  {c.disposition && c.disposition !== 'active' ? ` · ${c.disposition}` : ''}
                  {/* null is an UNKNOWN, not a clean bill (B-18). */}
                  {c.bounced === null ? ' · bounced —' : (c.bounced ? ' · BOUNCED' : '')}
                </>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </details>
  );
});

// RM :597-605. The bands are the server's `engaged / max_engaged` ratio; this
// file only turns a ratio into a hex.
export function stateFill(row, maxEngaged) {
  if (!row) return '#e0e0e0';                       // no data
  if (!row.engaged) return '#ffcdd2';               // sent, zero engaged
  const ratio = maxEngaged > 0 ? row.engaged / maxEngaged : 0;
  if (ratio < 0.25) return '#fde0dc';
  if (ratio < 0.5) return '#ef9a9a';
  if (ratio < 0.75) return '#e53935';
  return '#b71c1c';
}

export function pct(n, d) {
  if (!d) return '0%';
  return `${((n / d) * 100).toFixed(1)}%`;
}

// `rollupStatus`: 'ready' (default: today's behaviour, and a snapshot that is fresh or stale) |
// 'loading' | 'pending' | 'error'. Anything but 'ready' shows the stats strip and a plain panel,
// NEVER the tier counts or the map with zeros in them (G4: no invented empty report).
export default function CampaignReport({
  stats, rollup, tiers, shownDivisions, filters, toggleFilter, onReset,
  divisionTab, setDivisionTab, audience = 'staff', rollupStatus = 'ready',
}) {
  const player = audience === 'player';
  const ready = rollupStatus === 'ready';
  return (
    <div className="py-6 space-y-6">
      {ready ? <SnapshotNote snapshot={rollup?.snapshot} /> : null}
      {rollup?.truncated ? (
        <div className="rounded-2xl border-2 border-amber-500 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          Report truncated at 50,000 rows — numbers below are partial.
        </div>
      ) : null}

      {stats ? (
        <section className={`${PANEL} p-6`}>
          <div className="flex items-baseline gap-6 flex-wrap text-sm text-[#0E2A20]">
            <span>{stats.sent}/{stats.total} sent</span>
            <span>{stats.opened} opens ({stats.open_rate})</span>
            <span>{stats.clicked} clicks ({stats.click_rate})</span>
            {player ? null : <span>{stats.bounced} bounced</span>}
            <span>{stats.replied} replied</span>
          </div>
          {!player && stats.observed_opened != null ? (
            <p className={`${BODY_MUTED} text-xs mt-3`}>
              Open evidence: {stats.observed_opened} image loads observed · {stats.ambiguous_opened ?? 0} ambiguous · {stats.confirmed_viewed ?? 0} confirmed views
            </p>
          ) : null}
        </section>
      ) : null}

      {!ready ? <PendingPanel kind={rollupStatus} errorClass={rollup?.snapshot?.error_class} /> : (<>
      <section className={`${PANEL} p-6`}>
        <div className="flex items-baseline gap-6 flex-wrap">
          {player ? (
            <>
              <span className="text-sm text-[#0E2A20]">🔥 Hot {tiers.hot}</span>
              <span className="text-sm text-[#0E2A20]">◆ Interested {tiers.interested ?? 0}</span>
              <span className="text-sm text-[#0E2A20]">⚡ Warm {tiers.warm ?? 0}</span>
              <span className={BODY_MUTED}>{tiers.total} coaches engaged</span>
            </>
          ) : (
            <>
              <span className="text-sm text-[#0E2A20]">🔥 Hot {tiers.hot} ({pct(tiers.hot, tiers.total)})</span>
              <span className="text-sm text-[#0E2A20]">◆ Interested {tiers.interested ?? 0} ({pct(tiers.interested ?? 0, tiers.total)})</span>
              <span className="text-sm text-[#0E2A20]">⚡ Warm {tiers.warm} ({pct(tiers.warm, tiers.total)})</span>
              <span className="text-sm text-[#0E2A20]">Cold {tiers.cold} ({pct(tiers.cold, tiers.total)})</span>
              <span className={BODY_MUTED}>{tiers.tiered_total ?? tiers.total} tiered · {tiers.disqualified ?? 0} disqualified · {tiers.unreachable ?? 0} unreachable</span>
            </>
          )}
        </div>
        {!player && rollup?.unmatched_engagement > 0 ? (
          /* A quiet note, not an alarm — it is an unknown, not a fault. */
          <p className={`${BODY_MUTED} text-xs mt-3`}>
            {rollup.unmatched_engagement} engagement row{rollup.unmatched_engagement === 1 ? '' : 's'} had no message row.
          </p>
        ) : null}
      </section>

      {/* ── choropleth ────────────────────────────────────────────── */}
      <section className={`${PANEL} p-6`}>
        <h3 className={`${H2} mb-4`}>Positive interest by state</h3>
        <svg viewBox="0 0 959 593" className="w-full h-auto" role="img" aria-label="Positive interest by state">
          {Object.values(stateData).map((s) => {
            const row = (rollup?.by_state || []).find((r) => r.state === s.abbr);
            const active = filters.state.includes(s.abbr);
            return (
              <path
                key={s.abbr}
                d={s.d}
                fill={stateFill(row, rollup?.max_engaged || 0)}
                stroke={active ? '#1b4332' : '#ffffff'}
                strokeWidth={active ? 2 : 0.8}
                className="cursor-pointer"
                onClick={() => toggleFilter('state', s.abbr)}
              >
                <title>{player
                  ? `${s.name}: ${row?.engaged || 0} showing positive interest`
                  : `${s.name}: ${row?.sent || 0} sent, ${row?.engaged || 0} showing positive interest`}</title>
              </path>
            );
          })}
          {Object.values(stateData).map((s) => (
            <text
              key={`l-${s.abbr}`}
              x={s.labelX + (s.leader ? (s.dx || 0) : 0)}
              y={s.labelY + (s.leader ? (s.dy || 0) : 0)}
              textAnchor="middle"
              className="pointer-events-none"
              style={{ fontSize: 9, fill: '#333' }}
            >
              {s.abbr}
            </text>
          ))}
          {/* Leader lines for the NE states whose label sits outside the shape. */}
          {Object.values(stateData).filter((s) => s.leader).map((s) => (
            <line
              key={`ll-${s.abbr}`}
              x1={s.labelX} y1={s.labelY}
              x2={s.labelX + (s.dx || 0)} y2={s.labelY + (s.dy || 0) + 3}
              stroke="#999" strokeWidth="0.5" className="pointer-events-none"
            />
          ))}
        </svg>
        {/* The legend swatches are the stateFill() ramp — data, not chrome. */}
        <div className={`${DASH} flex items-center gap-3 flex-wrap mt-4 pt-4 ${LABEL} normal-case tracking-normal text-xs`}>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#e0e0e0' }} /> no data</span>
          {player ? null : (
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#ffcdd2' }} /> sent, no positive interest</span>
          )}
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#fde0dc' }} /> low</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#ef9a9a' }} /> </span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#e53935' }} /> </span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#b71c1c' }} /> high</span>
        </div>
      </section>

      {/* ── filter chips ──────────────────────────────────────────── */}
      <section className={`${PANEL} p-6 space-y-4`}>
        {['division', 'region', 'state'].map((key) => {
          const values = rollup?.filter_values?.[`${key}s`] || [];
          if (!values.length) return null;
          return (
            <div key={key}>
              <div className={`${LABEL} mb-2`}>{key}</div>
              <div className="flex gap-1.5 flex-wrap">
                {values.map((v) => (
                  <button
                    key={v} type="button" onClick={() => toggleFilter(key, v)}
                    className={pill(filters[key].includes(v))}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={onReset}
          className={`${pill(false)} text-[#5A5A5A] border-[#5A5A5A]`}
        >
          Reset
        </button>
      </section>

      {/* ── top 15s ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className={`${PANEL} p-6`}>
          <h3 className={`${H2} mb-4`}>Top Schools</h3>
          {(rollup?.top_schools || []).length === 0 ? <p className={BODY_MUTED}>—</p> : (
            <ol className="space-y-1.5">
              {rollup.top_schools.map((s, i) => (
                <li key={s.school} className="text-sm text-[#0E2A20] flex justify-between gap-3">
                  <span>{i + 1}. {s.school || '(unknown)'}</span>
                  <span className="text-[#5A5A5A]">{s.score != null ? `score ${s.score} · ` : ''}{s.sent}/{s.total} coaches · {s.opened} opens · {s.clicked} clicked</span>
                </li>
              ))}
            </ol>
          )}
        </section>
        <section className={`${PANEL} p-6`}>
          <h3 className={`${H2} mb-4`}>Top Coaches</h3>
          {(rollup?.top_coaches || []).length === 0 ? <p className={BODY_MUTED}>—</p> : (
            <ol className="space-y-1.5">
              {rollup.top_coaches.map((c, i) => (
                <li key={`${c.name}-${c.school}-${i}`} className="text-sm text-[#0E2A20] flex justify-between gap-3">
                  <span>{i + 1}. {c.tier === 'hot' ? '🔥' : c.tier === 'interested' ? '◆' : c.tier === 'warm' ? '⚡' : ''} {c.name} · {c.school}{c.title ? ` — ${c.title}` : ''}</span>
                  <span className="text-[#5A5A5A]">{c.score != null ? `score ${c.score}` : ''}{c.opened ? ' · viewed' : ''}{c.clicked ? ' · clicked' : ''}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* ── division tabs + tree ──────────────────────────────────── */}
      <section className={`${PANEL} p-6`}>
        <div className="flex gap-1.5 flex-wrap mb-5">
          <button type="button" onClick={() => setDivisionTab(null)}
            className={pill(divisionTab === null)}>
            All
          </button>
          {(rollup?.division_tabs || []).map((d) => (
            <button key={d} type="button" onClick={() => setDivisionTab(d)}
              className={pill(divisionTab === d)}>
              {d}
            </button>
          ))}
        </div>

        {shownDivisions.length === 0 ? (
          <p className={BODY_MUTED}>{player ? 'No coaches match these filters.' : 'No recipients match these filters.'}</p>
        ) : null}

        {shownDivisions.map((div) => (
          <details key={div.division} open className={`${DASH} mb-4 pt-4`}>
            <summary className="cursor-pointer font-display font-normal text-[#0E2A20] text-lg">
              {div.division} — {div.sent} sent · {div.opened} opens ({pct(div.opened, div.sent)}) · {div.clicked} clicked ({pct(div.clicked, div.sent)})
              {player ? null : ` · ${div.bounced} bounced`}
            </summary>
            <div className="pl-4 mt-3 space-y-2">
              {div.schools.map((s) => (
                <SchoolRow key={s.school} school={s} player={player} />
              ))}
            </div>
          </details>
        ))}
      </section>
      </>)}
    </div>
  );
}
